/**
 * jr-survey.js — the customer survey page (templates/page.survey.liquid).
 *
 * Talks to the public `jr-survey` edge function. The page never asks who you are:
 *   1. an invite token in the URL FRAGMENT (#i=…) from the emailed link, or
 *   2. failing that, the short-lived account token /apps/jr/ mints for a signed-in
 *      customer (the same one the help chat uses), or
 *   3. failing that, a Sign-in button that comes back here.
 *
 * The fragment is read once, kept in sessionStorage so a refresh still works, and
 * CLEARED from the address bar immediately — it is a personal link and has no business
 * sitting in the URL where a screenshot or a share sheet would carry it.
 *
 * Every answer saves as it is given, so someone who stops at question 3 still leaves
 * three answers. "Send my answers" only marks the response complete.
 *
 * All text is set with textContent / createElement. Nothing from the network is ever
 * written as HTML.
 */
(function () {
  'use strict';
  var host = document.querySelector('.jr-survey');
  var root = document.getElementById('jr-survey-root');
  if (!host || !root) return;

  var ENDPOINT = host.getAttribute('data-endpoint');
  var LOGIN_URL = host.getAttribute('data-login-url');
  var params = new URLSearchParams(window.location.search);
  var SLUG = (params.get('s') || host.getAttribute('data-default-survey') || '').toLowerCase();
  if (!/^[a-z0-9][a-z0-9-]{1,60}$/.test(SLUG)) SLUG = host.getAttribute('data-default-survey');
  var STORE_KEY = 'jr_survey:' + SLUG;

  var state = { invite: null, account: null, preview: null, survey: null, answers: {}, completed: false };

  // ── identity from the fragment, then out of the address bar ─────────────────
  function readFragment() {
    var saved = {};
    try { saved = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '{}') || {}; } catch (_) { saved = {}; }
    var h = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    var i = h.get('i'), pk = h.get('pk');
    if (i && /^[0-9a-f]{48}$/.test(i)) saved.invite = i;
    if (pk && pk.length <= 200) saved.preview = pk;
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(saved)); } catch (_) { /* private mode */ }
    if (window.location.hash) {
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (_) {}
    }
    state.invite = saved.invite || null;
    state.preview = saved.preview || null;
  }

  function accountToken() {
    return fetch('/apps/jr/', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ action: 'help_token' })
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { return j && j.ok && j.token ? j.token : null; })
      .catch(function () { return null; });
  }

  function api(body) {
    var headers = { 'Content-Type': 'application/json' };
    if (state.preview) headers['x-survey-preview'] = state.preview;
    body.slug = SLUG;
    if (state.invite) body.invite = state.invite; else if (state.account) body.account = state.account;
    return fetch(ENDPOINT, { method: 'POST', headers: headers, body: JSON.stringify(body) })
      .then(function (r) { return r.json().catch(function () { return { ok: false, error: 'Something went wrong. Please try again.' }; }); });
  }

  // An account token lasts 15 minutes; a slow reader outlives it. Re-mint once.
  function apiWithRefresh(body) {
    return api(body).then(function (res) {
      if (res && res.need_identity && !state.invite) {
        return accountToken().then(function (t) {
          if (!t) return res;
          state.account = t;
          return api(body);
        });
      }
      return res;
    });
  }

  // ── tiny DOM helpers ────────────────────────────────────────────────────────
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }

  function showMessage(title, body, action) {
    clear(root);
    var wrap = el('div', 'jr-survey__done');
    wrap.appendChild(el('h1', 'jr-survey__title', title));
    if (body) wrap.appendChild(el('p', 'jr-survey__intro', body));
    if (action) {
      var p = el('p'); p.style.marginTop = '28px';
      var a = el(action.href ? 'a' : 'button', 'jr-survey__btn' + (action.quiet ? ' jr-survey__btn--quiet' : ''), action.label);
      if (action.href) a.href = action.href; else { a.type = 'button'; a.addEventListener('click', action.onClick); }
      p.appendChild(a); wrap.appendChild(p);
    }
    root.appendChild(wrap);
  }

  // ── saving ──────────────────────────────────────────────────────────────────
  var statusEls = {};
  // Per-question "collect whatever is in a field that has not fired change yet", called
  // on submit. Only questions with a typed box register one.
  var flushers = {};
  function setStatus(id, text, isError) {
    var s = statusEls[id]; if (!s) return;
    s.textContent = text || '';
    s.className = 'jr-q__status' + (isError ? ' is-error' : '');
  }
  // Saves go ONE AT A TIME. Two answers tapped in the same instant used to reach the
  // backend together, contend on its rate limiter, and both fail. A queue also
  // guarantees the answers merge in the order they were given. A save the backend
  // marks `retry` (it was NOT stored) is sent once more after a beat before the
  // customer is bothered with it.
  var queue = Promise.resolve();
  function enqueue(job) {
    var next = queue.then(job, job);
    queue = next.catch(function () {});
    return next;
  }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function send(body) {
    return apiWithRefresh(body).then(function (res) {
      if (res && res.retry) return wait(1200).then(function () { return apiWithRefresh(body); });
      return res;
    });
  }
  function saveOne(id, value) {
    state.answers[id] = value;
    var patch = {}; patch[id] = value;
    setStatus(id, 'Saving…');
    return enqueue(function () {
      return send({ action: 'save', answers: patch, complete: false }).then(function (res) {
        if (res && res.ok) { setStatus(id, 'Saved'); return true; }
        var msg = (res && res.errors && res.errors[id]) || (res && res.error) || 'Could not save that.';
        setStatus(id, msg, true);
        return false;
      });
    });
  }

  // ── question renderers ──────────────────────────────────────────────────────
  // A saved answer is a value for `single` and a list for `multi`; either may carry ONE
  // { other: "typed text" }, which is the shape the backend validates. Both are read the
  // same way here so restoring an answer never has to care which kind of question it was.
  function heldValues(held) { return Array.isArray(held) ? held.filter(function (x) { return typeof x === 'string'; }) : []; }
  function heldOther(held) {
    if (Array.isArray(held)) {
      for (var i = 0; i < held.length; i++) if (held[i] && typeof held[i] === 'object' && held[i].other) return String(held[i].other);
      return '';
    }
    return held && typeof held === 'object' && held.other ? String(held.other) : '';
  }

  function renderChoice(q, box, multi) {
    var held = state.answers[q.id];
    var vals = heldValues(held), other0 = heldOther(held);
    var otherInput = null, otherBox = null;
    var opts = el('div', 'jr-opts');
    function otherText() { return otherInput ? otherInput.value.trim().slice(0, 120) : ''; }
    // What this question would save right now. The typed box rides along as the last
    // element, so a show someone named is part of the same answer as the boxes they
    // ticked — not a second answer that overwrites them.
    function picked() {
      var out = [];
      opts.querySelectorAll('input:checked').forEach(function (i) {
        if (i.value === '__other') { var t = otherText(); if (t) out.push({ other: t }); return; }
        out.push(i.value);
      });
      return out;
    }
    function sync() {
      if (!multi) return;
      // Counted from the TICKS, not from picked(): "Something else" occupies its slot the
      // moment it is ticked, before anything has been typed into it.
      var full = q.max && opts.querySelectorAll('input:checked').length >= q.max;
      opts.querySelectorAll('input').forEach(function (i) { i.disabled = !!full && !i.checked; });
    }
    (q.options || []).concat(q.other ? [{ v: '__other', l: q.other_label || 'Something else' }] : []).forEach(function (o) {
      var lab = el('label', 'jr-opt');
      var inp = document.createElement('input');
      inp.type = multi ? 'checkbox' : 'radio';
      inp.name = 'jrq-' + q.id; inp.value = o.v; inp.id = 'jrq-' + q.id + '-' + o.v;
      if (o.v === '__other') { inp.checked = !!other0; otherBox = inp; }
      else if (multi) inp.checked = vals.indexOf(o.v) >= 0;
      else inp.checked = held === o.v;
      inp.addEventListener('change', function () {
        if (o.v === '__other' && otherInput) {
          otherInput.hidden = multi ? !inp.checked : false;
          if (!otherInput.hidden) otherInput.focus();
        } else if (!multi && otherInput) otherInput.hidden = true;
        if (multi) { sync(); saveOne(q.id, picked()); return; }
        if (o.v === '__other') return;   // nothing to save until they type it
        saveOne(q.id, o.v);
      });
      lab.appendChild(inp); lab.appendChild(el('span', null, o.l));
      opts.appendChild(lab);
    });
    box.appendChild(opts);
    if (q.other) {
      var hint = q.other_placeholder || 'Tell us what';
      otherInput = el('input', 'jr-input jr-other');
      otherInput.type = 'text'; otherInput.maxLength = 120; otherInput.id = 'jrq-' + q.id + '-other-text';
      otherInput.setAttribute('aria-label', hint);
      otherInput.placeholder = hint;
      otherInput.hidden = !other0;
      if (other0) otherInput.value = other0;
      otherInput.addEventListener('change', function () {
        if (multi) { saveOne(q.id, picked()); return; }   // emptying it clears the answer
        var v = otherText();
        if (v) saveOne(q.id, { other: v });
      });
      box.appendChild(otherInput);
      // Typing into the box and hitting Send without leaving it fires no `change`, the
      // same trap the text questions have. Collected at submit instead of lost.
      flushers[q.id] = function () {
        var t = otherText();
        if (!t || otherInput.hidden || !otherBox || !otherBox.checked) return undefined;
        return multi ? picked() : { other: t };
      };
    }
    sync();
  }

  function renderScale(q, box) {
    var held = state.answers[q.id];
    var row = el('div', 'jr-opts jr-scale');
    for (var n = (q.min || 1); n <= (q.max || 5); n++) {
      (function (n) {
        var lab = el('label', 'jr-opt');
        var inp = document.createElement('input');
        inp.type = 'radio'; inp.name = 'jrq-' + q.id; inp.value = String(n); inp.id = 'jrq-' + q.id + '-' + n;
        inp.checked = held === n;
        inp.addEventListener('change', function () { saveOne(q.id, n); });
        lab.appendChild(inp); lab.appendChild(el('span', null, String(n)));
        row.appendChild(lab);
      })(n);
    }
    box.appendChild(row);
    if (q.low || q.high) {
      var ends = el('div', 'jr-scale__ends');
      ends.appendChild(el('span', null, q.low || '')); ends.appendChild(el('span', null, q.high || ''));
      box.appendChild(ends);
    }
  }

  function renderYear(q, box) {
    var inp = el('input', 'jr-input jr-input--year');
    inp.type = 'text'; inp.inputMode = 'numeric'; inp.autocomplete = 'bday-year';
    inp.maxLength = 4; inp.placeholder = 'YYYY'; inp.id = 'jrq-' + q.id;
    if (state.answers[q.id]) inp.value = String(state.answers[q.id]);
    inp.addEventListener('input', function () { inp.value = inp.value.replace(/[^0-9]/g, '').slice(0, 4); });
    inp.addEventListener('change', function () {
      if (!inp.value) return;
      var y = Number(inp.value);
      if (inp.value.length !== 4 || y < (q.min || 1900) || y > (q.max || 2100)) { setStatus(q.id, 'Enter a four-digit year, like 1968.', true); return; }
      saveOne(q.id, y);
    });
    box.appendChild(inp);
    return inp;
  }

  function renderText(q, box) {
    var long = (q.max || 600) > 200;
    var inp = el(long ? 'textarea' : 'input', long ? 'jr-textarea' : 'jr-input');
    if (!long) inp.type = 'text';
    inp.maxLength = q.max || 600; inp.id = 'jrq-' + q.id;
    if (state.answers[q.id]) inp.value = String(state.answers[q.id]);
    inp.addEventListener('change', function () { saveOne(q.id, inp.value.trim()); });
    box.appendChild(inp);
    return inp;
  }

  function renderSurvey() {
    var sv = state.survey;
    clear(root); statusEls = {}; flushers = {};
    var head = el('header', 'jr-survey__head');
    head.appendChild(el('p', 'jr-survey__eyebrow', "Jeremy's Razors"));
    head.appendChild(el('h1', 'jr-survey__title', sv.title));
    if (sv.intro) head.appendChild(el('p', 'jr-survey__intro', sv.intro));
    root.appendChild(head);

    var form = el('form'); form.noValidate = true;
    (sv.questions || []).forEach(function (q, ix) {
      var grouped = q.type === 'single' || q.type === 'multi' || q.type === 'scale';
      var box = el(grouped ? 'fieldset' : 'div', 'jr-q');
      var label = el(grouped ? 'legend' : 'label', 'jr-q__label');
      if (!grouped) label.htmlFor = 'jrq-' + q.id;
      label.appendChild(el('span', 'jr-q__num', (ix + 1) + '.'));
      label.appendChild(document.createTextNode(q.label));
      box.appendChild(label);
      if (q.type === 'single') renderChoice(q, box, false);
      else if (q.type === 'multi') renderChoice(q, box, true);
      else if (q.type === 'scale') renderScale(q, box);
      else if (q.type === 'year') renderYear(q, box);
      else renderText(q, box);
      var st = el('p', 'jr-q__status'); st.setAttribute('role', 'status');
      statusEls[q.id] = st; box.appendChild(st);
      form.appendChild(box);
    });

    var foot = el('div', 'jr-survey__foot');
    var msg = el('p', 'jr-survey__msg'); msg.hidden = true; foot.appendChild(msg);
    var btn = el('button', 'jr-survey__btn', 'Send my answers'); btn.type = 'submit';
    foot.appendChild(btn);
    foot.appendChild(el('p', 'jr-survey__note', 'Skip anything you would rather not answer. Your answers are saved as you go.'));
    form.appendChild(foot);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // A field that still has focus has not fired `change` yet — collect it. That is
      // both the written questions and any "something else" box someone typed into and
      // sent straight from.
      var patch = {};
      (sv.questions || []).forEach(function (q) {
        if (q.type !== 'text') return;
        var f = document.getElementById('jrq-' + q.id);
        if (f && f.value.trim()) patch[q.id] = f.value.trim();
      });
      Object.keys(flushers).forEach(function (id) {
        var v = flushers[id]();
        if (v !== undefined) patch[id] = v;
      });
      btn.disabled = true; btn.textContent = 'Sending…'; msg.hidden = true;
      enqueue(function () { return send({ action: 'save', answers: Object.assign({}, state.answers, patch), complete: true }); }).then(function (res) {
        if (res && res.ok) { showMessage(sv.thanks || 'Thank you.', 'Your answers are in. You can close this page.'); return; }
        btn.disabled = false; btn.textContent = 'Send my answers';
        msg.textContent = (res && res.error) || 'That did not go through. Please try again.'; msg.hidden = false;
        if (res && res.errors) Object.keys(res.errors).forEach(function (k) { setStatus(k, res.errors[k], true); });
      });
    });
    root.appendChild(form);
  }

  // ── boot ────────────────────────────────────────────────────────────────────
  function load() {
    return apiWithRefresh({ action: 'get' }).then(function (res) {
      if (!res || !res.ok) {
        if (res && res.need_identity) {
          showMessage('Sign in to take the survey',
            'We match your answers to your account so we never have to ask who you are. Sign in and you will come straight back here.',
            { label: 'Sign in', href: LOGIN_URL + encodeURIComponent('?s=' + SLUG) });
        } else {
          showMessage('This survey is not available', (res && res.error === 'This survey has closed.') ? 'It has closed. Thank you for your interest.' : 'The link may be out of date.');
        }
        return;
      }
      state.survey = res.survey;
      state.answers = res.answers || {};
      state.completed = !!res.completed;
      if (res.survey.closed) { showMessage('This survey has closed', 'Thank you to everyone who answered.'); return; }
      if (state.completed) {
        showMessage(res.survey.thanks || 'Thank you.', 'You have already sent us your answers.',
          { label: 'Change my answers', quiet: true, onClick: renderSurvey });
        return;
      }
      renderSurvey();
    });
  }

  readFragment();
  if (state.invite) { load(); return; }
  accountToken().then(function (t) { state.account = t; return load(); });
})();
